import { formatObservationValue } from '@medplum/core'
import { formatDateTime } from '@seen/core/dates'
import { invariant } from '@seen/core/invariant'
import { ExtendedObservation } from '@seen/core/models/observation'
import type { DefaultError } from '@tanstack/react-query'
import type { JSX } from 'react'
import { useNavigate, useParams } from 'react-router'
import { DropdownMenuDeleteItem } from '#src/components/core/dropdown-menu/DropdownMenuDeleteItem.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { ScrollAreaContent } from '#src/components/core/scroll-area/ScrollAreaContent.tsx'
import { ScrollAreaHeader } from '#src/components/core/scroll-area/ScrollAreaHeader.tsx'
import { PageLayout } from '#src/components/display/PageLayout.tsx'
import { PractitionerDisplay } from '#src/components/display/PractitionerDisplay.tsx'
import { DropdownMenuMedplumLinkItem } from '#src/components/DropdownMenuMedplumLinkItem.tsx'
import { MoreDropdownMenu } from '#src/components/MoreDropdownMenu.tsx'
import { ObservationHistoryChart } from '#src/components/observation/ObservationHistoryChart.tsx'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { toast } from '#src/utils/toast.ts'

// onDelete replaces leaving the page once its observation is deleted (an
// embedding host closes its own view).
export function ParticipantObservationPage({
  onDelete,
}: {
  onDelete?: () => void
}): JSX.Element {
  const { observationId } = useParams()
  invariant(observationId, 'Observation ID is required')
  return (
    <ParticipantObservationPageComponent
      key={observationId}
      observationId={observationId}
      onDelete={onDelete}
    />
  )
}

function ParticipantObservationPageComponent({
  observationId,
  onDelete,
}: {
  observationId: string
  onDelete?: () => void
}): JSX.Element {
  const navigate = useNavigate()
  const mutate = usePatchMutation<ExtendedObservation>().mutateAsync
  const { data: observation, isLoading } = useRead(
    ExtendedObservation,
    observationId
  )
  if (isLoading) {
    return <Loader />
  }

  invariant(observation, `Observation not found: ${observationId}`)

  const coding = observation.codeableConcept.coding?.[0]
  const subject = observation.subject

  return (
    <PageLayout contentClassName="mx-auto w-full max-w-[calc(76ch+3rem)]">
      <ScrollAreaHeader className="-mx-4 mt-2 flex items-center justify-between gap-1">
        <h2 className="text-2xl font-semibold">
          {observation.display ?? 'Observation'}
        </h2>
        <MoreDropdownMenu>
          <DropdownMenuDeleteItem
            deleteConfirmationProps={{
              title: `Delete "${observation.display ?? 'observation'}"?`,
              description:
                'This will mark the observation as entered-in-error. It can still be recovered by contacting our team.',
              onConfirm: () => {
                if (onDelete) {
                  onDelete()
                } else {
                  void navigate(-1)
                }
                mutate({
                  instance: observation,
                  mutateFn: (obs) => {
                    obs.markAsEnteredInError()
                  },
                })
                  .then(() =>
                    toast.success(
                      `${observation.display ?? 'Observation'} deleted`
                    )
                  )
                  .catch((error: DefaultError) => {
                    toast.error(
                      `Failed to delete ${observation.display ?? 'observation'}: ${error}`
                    )
                  })
              },
            }}
          />
          <DropdownMenuMedplumLinkItem to={observation} />
        </MoreDropdownMenu>
      </ScrollAreaHeader>
      <ScrollAreaContent className="gap-6 p-0">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 text-sm">
            {observation.effectiveDateTime && (
              <span>{formatDateTime(observation.effectiveDateTime)}</span>
            )}
            {observation.status && (
              <span className="text-foreground-tertiary capitalize">
                {observation.status}
              </span>
            )}
          </div>
          <div className="text-lg font-semibold">
            {formatObservationValue(observation.toFhirResource())}
          </div>
          {observation.performingPractitioner && (
            <div className="text-sm text-foreground-secondary">
              Recorded by:{' '}
              <PractitionerDisplay
                practitioner={observation.performingPractitioner}
              />
            </div>
          )}
          {coding && (
            <div className="text-sm text-foreground-secondary">
              {coding.code && (
                <div>
                  Code: {coding.code}
                  {coding.system && ` (${coding.system})`}
                </div>
              )}
            </div>
          )}
        </div>
        {coding && subject && (
          <div className="mt-4">
            <h3 className="mb-2 text-sm font-medium">History</h3>
            <ObservationHistoryChart
              subject={subject}
              coding={coding}
              observation={observation}
              variant="full"
            />
          </div>
        )}
      </ScrollAreaContent>
    </PageLayout>
  )
}
